"use client"

import { useEffect, useRef } from "react"
import type { AutomationTriggerButton } from "@/schema"
import { Card, CardDescription, CardTitle, ScrollArea, Skeleton } from "@ui/shad"
import { cn } from "@ui/utils"
import { ArrowRight, ChevronLeft, ChevronRight, Wand } from "lucide-react"

import { useFetchAutomationTriggerButtons } from "@/hooks/api/automation-trigger-buttons"
import { useScrollNavigation } from "@/hooks/use-scroll-navigation"
import { getIconComponent } from "@/components/automations/edit-panel/triggers/icon-color-picker-input"
import { Link } from "@/components/book-link"

import ChatGreeting from "./chat-greeting"

interface MagicButtonCardProps {
  trigger: AutomationTriggerButton
  onClick: () => void
  isLoading?: boolean
}

const MagicButtonCardSkeleton = () => (
  <Card className="bg-tinted short:h-auto short:w-44 short:gap-1.5 short:px-3 short:py-3 flex h-56 w-48 flex-none flex-col items-start gap-2 border-none px-4 pt-4 pb-3 shadow-none 2xl:h-64 2xl:w-56">
    <Skeleton className="short:size-8 size-10 flex-none rounded-md" />
    <div className="short:pt-0 w-full space-y-2 pt-1">
      <Skeleton className="h-5 w-3/4" />
      <div className="short:hidden space-y-1.5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
    <div className="short:hidden flex w-full grow flex-col items-end justify-end">
      <Skeleton className="h-4 w-24" />
    </div>
  </Card>
)

const DEFAULT_MAGIC_BUTTON_COLOR = "#6366F1" // indigo from Colorable

const MagicButtonCard = ({ trigger, onClick, isLoading }: MagicButtonCardProps) => {
  const IconComponent = getIconComponent(trigger.icon ?? undefined)
  const color = trigger.color || DEFAULT_MAGIC_BUTTON_COLOR

  return (
    <div
      style={
        {
          "--magic-button-color": color,
          "--magic-button-color-background": `${color}10`,
        } as React.CSSProperties
      }
    >
      <Card
        className={cn(
          "bg-tinted flex h-56 w-48 flex-none flex-col items-start gap-2 border-none px-4 pt-4 pb-3 shadow-none ring-2 ring-transparent transition-all duration-200 hover:bg-(--magic-button-color-background)! hover:ring-(--magic-button-color) 2xl:h-64 2xl:w-56",
          "short:h-auto short:w-44 short:gap-1.5 short:px-3 short:py-3",
          isLoading && "pointer-events-none opacity-50"
        )}
        variant="clickable"
        onClick={onClick}
      >
        <div className="short:size-8 flex size-10 flex-none items-center justify-center rounded-md bg-(--magic-button-color)">
          <IconComponent className="short:size-4 size-5 text-white" strokeWidth={1.5} />
        </div>
        <CardTitle className="short:text-sm text-base/tight">{trigger.title}</CardTitle>
        <div className="short:hidden min-h-0 flex-1">
          <ScrollArea className="h-full w-full">
            <div className="pr-3">
              {trigger.description && <CardDescription className="text-sm/snug">{trigger.description}</CardDescription>}
            </div>
          </ScrollArea>
        </div>
        <div className="short:hidden flex w-full flex-none flex-col items-end justify-end">
          <Link
            href={`/automations/${trigger.automation_id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 border-b border-transparent text-xs transition-all hover:border-current"
          >
            View automation
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </Card>
    </div>
  )
}

export interface MagicButtonsSectionProps {
  hideGreeting: boolean
  hasStartedChat: boolean
  recordType?: "client" | "prospect" | "contact" | "business" | "meeting"
  recordId?: string
  onMagicButtonClick?: (automationId: string, title: string) => void
  onButtonsResolved?: (hasButtons: boolean) => void
  className?: string
}

export function MagicButtonsSection({
  hideGreeting,
  hasStartedChat,
  recordType,
  recordId,
  onMagicButtonClick,
  onButtonsResolved,
  className,
}: MagicButtonsSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, scroll } = useScrollNavigation(scrollRef)

  const { data: buttonsData, isLoading } = useFetchAutomationTriggerButtons(recordType, recordId, {
    enabled: !!recordType && !!recordId,
  })

  const buttons = buttonsData?.automation_trigger_buttons ?? []

  useEffect(() => {
    if (isLoading) return

    onButtonsResolved?.(buttons.length > 0)
  }, [buttons.length, isLoading, onButtonsResolved])

  const handleTrigger = async (trigger: AutomationTriggerButton) => {
    if (!recordId) return
    if (!onMagicButtonClick) return

    onMagicButtonClick(trigger.automation_id, trigger.title)
  }

  if (recordType && isLoading) {
    return (
      <div
        className={cn(
          "flex w-full max-w-full grow flex-col items-center justify-center-safe px-8 2xl:max-w-7xl",
          className
        )}
      >
        {!hideGreeting && <ChatGreeting hasStartedChat={hasStartedChat} />}
        <div className="text-muted-foreground short:mt-2 short:mb-1 shorter:hidden mt-4 mb-1.5 flex w-full items-center gap-1 px-2 text-[13px] font-medium tracking-tight uppercase">
          <Wand className="text-icon size-4" />
          Magic buttons
        </div>
        <div className="flex w-full gap-4 px-1">
          <MagicButtonCardSkeleton />
          <MagicButtonCardSkeleton />
        </div>
      </div>
    )
  }

  if (!recordType || buttons.length === 0) {
    return (
      <div className={cn("w-full", className)}>{!hideGreeting && <ChatGreeting hasStartedChat={hasStartedChat} />}</div>
    )
  }

  return (
    <div
      className={cn(
        "flex w-full max-w-full grow flex-col items-center justify-center-safe px-8 2xl:max-w-7xl",
        className
      )}
    >
      {!hideGreeting && <ChatGreeting hasStartedChat={hasStartedChat} />}
      <div className="text-muted-foreground short:mt-2 short:mb-1 shorter:hidden mt-4 mb-1.5 flex w-full items-center gap-1 px-2 text-[13px] font-medium tracking-tight uppercase">
        <Wand className="text-icon size-4" />
        Magic buttons
      </div>
      <div className="relative w-full">
        <div
          onClick={() => scroll("left")}
          className={cn(
            "text-muted-foreground hover:text-foreground absolute top-1/2 -left-8 -translate-y-1/2 cursor-pointer p-1 transition-opacity",
            canScrollLeft ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <ChevronLeft className="size-6" />
        </div>

        <div
          ref={scrollRef}
          className="flex w-full gap-4 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {buttons.map((button) => (
            <MagicButtonCard key={button.id} trigger={button} onClick={() => handleTrigger(button)} />
          ))}
        </div>

        <div
          onClick={() => scroll("right")}
          className={cn(
            "text-muted-foreground hover:text-foreground absolute top-1/2 -right-8 -translate-y-1/2 cursor-pointer p-1 transition-opacity",
            canScrollRight ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <ChevronRight className="size-6" />
        </div>
      </div>
    </div>
  )
}
